import api from '@/services/api';
import type { Tire, TireFormData, TireResponse, TiresResponse, BulkTireFormData } from '@/types/tire';

const API_BASE = '/customer/tires';

export const tireService = {
  async getTires(): Promise<Tire[]> {
    const response = await api.get<TiresResponse>(API_BASE);
    return response.data.data || [];
  },

  async createTire(data: TireFormData, receiptPhotos: File[]): Promise<Tire> {
    const formData = new FormData();
    formData.append('vehicle_idfk', data.vehicle_idfk.toString());
    formData.append('tire_position', data.tire_position);
    formData.append('pattern', data.pattern);
    formData.append('tire_size', data.tire_size);
    formData.append('dot', data.dot);
    formData.append('shop', data.shop);
    formData.append('code', data.code);
    formData.append('invoice_number', data.invoice_number);
    receiptPhotos.forEach((photo) => {
      formData.append('receipt_photos[]', photo);
    });
    const response = await api.post<TireResponse>(API_BASE, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
    });
    if (!response.data.data) throw new Error('Failed to create tire');
    return response.data.data;
  },

  async createBulkTires(data: BulkTireFormData): Promise<Tire[]> {
    const formData = new FormData();

    // Shared registration-level fields
    formData.append('shop', data.shop);
    formData.append('code', data.code);
    formData.append('invoice_number', data.invoice_number);
    formData.append('purchase_date', data.purchase_date);

    // Shared receipt photos (attached to first tire on the backend)
    data.receipt_photos.forEach((photo) => {
      formData.append('receipt_photos_shared[]', photo);
    });

    // Per-tire fields
    data.tires.forEach((tire, index) => {
      formData.append(`tires[${index}][vehicle_idfk]`, tire.vehicle_idfk.toString());
      formData.append(`tires[${index}][tire_position]`, tire.tire_position);
      formData.append(`tires[${index}][pattern]`, tire.pattern);
      formData.append(`tires[${index}][tire_size]`, tire.tire_size);
      formData.append(`tires[${index}][dot]`, tire.dot);
      tire.tire_photos.forEach((photo) => {
        formData.append(`tire_photos_${index}[]`, photo);
      });
    });

    const response = await api.post<{ success: boolean; message: string; data: Tire[] }>(
      API_BASE,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 }
    );
    if (!response.data.data) throw new Error('Failed to create tires');
    return response.data.data;
  },

  async getTire(tireId: number): Promise<Tire> {
    const response = await api.get<TireResponse>(`${API_BASE}/${tireId}`);
    if (!response.data.data) throw new Error('Tire not found');
    return response.data.data;
  },

  async updateTire(tireId: number, data: Omit<TireFormData, 'number_of_purchased'>, newTirePhotos?: File[]): Promise<Tire> {
    const formData = new FormData();
    formData.append('pattern', data.pattern);
    formData.append('tire_size', data.tire_size);
    formData.append('dot', data.dot);
    if (newTirePhotos && newTirePhotos.length > 0) {
      newTirePhotos.forEach((photo) => {
        formData.append('tire_photos[]', photo);
      });
    }
    const response = await api.post<TireResponse>(`${API_BASE}/${tireId}?_method=PUT`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
    });
    if (!response.data.data) throw new Error('Failed to update tire');
    return response.data.data;
  },

  async deleteTire(tireId: number): Promise<void> {
    await api.delete(`${API_BASE}/${tireId}`);
  },

  async deleteReceiptPhoto(photoId: number): Promise<void> {
    await api.delete(`/customer/receipt-photos/${photoId}`);
  },

  async deleteTirePhoto(photoId: number): Promise<void> {
    await api.delete(`/customer/tire-photos/${photoId}`);
  },

  async getPendingTires(params?: {
    status?: string;
    brand?: string;
    search?: string;
    page?: number;
    per_page?: number;
  }): Promise<{ data: Tire[]; meta: { current_page: number; last_page: number; per_page: number; total: number } }> {
    const response = await api.get<{
      success: boolean;
      data: Tire[];
      meta: { current_page: number; last_page: number; per_page: number; total: number };
    }>('/admin/tires/pending', { params });
    return {
      data: response.data.data || [],
      meta: response.data.meta || { current_page: 1, last_page: 1, per_page: 10, total: 0 },
    };
  },

  async approveTire(tireId: number): Promise<Tire> {
    const response = await api.post<TireResponse>(`/admin/tires/${tireId}/approve`);
    if (!response.data.data) throw new Error('Failed to approve tire');
    return response.data.data;
  },

  async rejectTire(tireId: number, reason: string): Promise<Tire> {
    const response = await api.post<TireResponse>(`/admin/tires/${tireId}/reject`, {
      rejection_reason: reason,
    });
    if (!response.data.data) throw new Error('Failed to reject tire');
    return response.data.data;
  },
};
